import { KeyboardEvent, ReactNode, useState } from "react"
import { Chip, InputBase, MenuItem, Stack, TextField, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { toEnumValue } from "../../../../../../../lib/lang"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedEnum, LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import type { TranslationKey } from "../../../../../../localization/translations"
import { StandardSegmentedControl } from "../../../../../../../shared/components/segmentedControl/StandardSegmentedControl"

import {
    AI_SUGGESTED_INGREDIENTS,
    AiDaySettings,
    AiHealthiness,
    AiMenuStyle,
    AiMenuTheme,
    AiSpiceLevel,
} from "../../weekMenuAiBank"

const useStyles = makeStyles()((theme) => ({
    fieldLabel: {
        color: theme.palette.tailwind.slate[950],
    },
    select: {
        "& .MuiOutlinedInput-root": {
            borderRadius: ".75rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
    },
    chipsBox: {
        minHeight: "2.75rem",
        padding: ".625rem .75rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        alignItems: "center",
    },
    ingredientChip: {
        height: "1.5rem",
        borderRadius: ".375rem",
        background: theme.palette.tailwind.violet[100],
        color: theme.palette.tailwind.violet[700],
        fontWeight: 600,
    },
    chipsInput: {
        flexGrow: 1,
        minWidth: "6rem",
    },
    suggestionChip: {
        height: "1.625rem",
        borderRadius: ".25rem",
        border: `1px dashed ${theme.palette.tailwind.gray[400]}`,
        background: theme.palette.background.paper,
        color: theme.palette.tailwind.slate[600],
        fontWeight: 500,
        cursor: "pointer",
        "&:hover": {
            borderColor: theme.palette.tailwind.violet[400],
            color: theme.palette.tailwind.violet[700],
        },
    },
}))

type FieldProps = Readonly<{
    labelId: TranslationKey
    labelFallback: string
    children: ReactNode
}>

/** Label above the control, shared by every field of the day generation card. */
function Field({ labelId, labelFallback, children }: FieldProps) {
    const { classes } = useStyles()

    return (
        <Stack spacing=".25rem" alignItems="flex-start" width="100%">
            <LocalizedStrict id={labelId}>
                <Typography variant="subtitle1" className={classes.fieldLabel}>
                    {labelFallback}
                </Typography>
            </LocalizedStrict>
            {children}
        </Stack>
    )
}

type Props = Readonly<{
    settings: AiDaySettings
    onChange: (settings: AiDaySettings) => void
}>

/** AI generation settings: theme, spice, healthiness, style, ingredients and notes. */
export function AiSettingsFields({ settings, onChange }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const [ingredientInput, setIngredientInput] = useState("")

    const update = (partial: Partial<AiDaySettings>) => onChange({ ...settings, ...partial })

    const toOptions = <T extends string>(values: ReadonlyArray<T>, translationBase: string) =>
        values.map((value) => ({ value, label: l10n.getStringForEnum(translationBase, value, value) }))

    const addIngredient = (raw: string) => {
        const value = raw.trim()
        if (!value || settings.ingredients.includes(value)) return
        update({ ingredients: [...settings.ingredients, value] })
        setIngredientInput("")
    }

    const handleIngredientKeyDown = (event: KeyboardEvent) => {
        if (event.key !== "Enter") return
        event.preventDefault()
        addIngredient(ingredientInput)
    }

    const handleThemeChange = (value: string) => {
        const theme = toEnumValue(AiMenuTheme, value)
        if (theme) update({ theme })
    }

    const handleStyleChange = (value: string) => {
        const style = toEnumValue(AiMenuStyle, value)
        if (style) update({ style })
    }

    return (
        <Stack spacing="1.5rem">
            <Field labelId="merchant-week-menu-ai-theme-label" labelFallback="Theme">
                <TextField
                    select
                    value={settings.theme}
                    fullWidth
                    className={classes.select}
                    onChange={(event) => handleThemeChange(event.target.value)}
                >
                    {Object.values(AiMenuTheme).map((theme) => (
                        <MenuItem key={theme} value={theme}>
                            <LocalizedEnum base="merchant-week-menu-ai-theme" enumValue={theme}>
                                <span>{theme}</span>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </TextField>
            </Field>

            <Field labelId="merchant-week-menu-ai-spice-label" labelFallback="Spice level">
                <StandardSegmentedControl
                    value={settings.spice}
                    onChange={(spice) => update({ spice })}
                    options={toOptions(Object.values(AiSpiceLevel), "merchant-week-menu-ai-spice")}
                    color="purple"
                />
            </Field>

            <Field labelId="merchant-week-menu-ai-health-label" labelFallback="Healthiness">
                <StandardSegmentedControl
                    value={settings.healthiness}
                    onChange={(healthiness) => update({ healthiness })}
                    options={toOptions(Object.values(AiHealthiness), "merchant-week-menu-ai-health")}
                    color="purple"
                />
            </Field>

            <Field labelId="merchant-week-menu-ai-style-label" labelFallback="Style">
                <TextField
                    select
                    value={settings.style}
                    fullWidth
                    className={classes.select}
                    onChange={(event) => handleStyleChange(event.target.value)}
                >
                    {Object.values(AiMenuStyle).map((style) => (
                        <MenuItem key={style} value={style}>
                            <LocalizedEnum base="merchant-week-menu-ai-style" enumValue={style}>
                                <span>{style}</span>
                            </LocalizedEnum>
                        </MenuItem>
                    ))}
                </TextField>
            </Field>

            <Stack spacing=".75rem">
                <Field labelId="merchant-week-menu-ai-ingredients-label" labelFallback="Ingredients to include">
                    <Stack direction="row" flexWrap="wrap" gap=".375rem" width="100%" className={classes.chipsBox}>
                        {settings.ingredients.map((ingredient) => (
                            <Chip
                                key={ingredient}
                                label={ingredient}
                                className={classes.ingredientChip}
                                onDelete={() =>
                                    update({
                                        ingredients: settings.ingredients.filter((value) => value !== ingredient),
                                    })
                                }
                            />
                        ))}
                        <LocalizedStrict id="merchant-week-menu-ai-ingredients-input" attrs={{ placeholder: true }}>
                            <InputBase
                                value={ingredientInput}
                                placeholder="Type and press enter..."
                                className={classes.chipsInput}
                                onChange={(event) => setIngredientInput(event.target.value)}
                                onKeyDown={handleIngredientKeyDown}
                            />
                        </LocalizedStrict>
                    </Stack>
                </Field>
                <Stack direction="row" flexWrap="wrap" gap=".5rem">
                    {AI_SUGGESTED_INGREDIENTS.filter((suggestion) => !settings.ingredients.includes(suggestion)).map(
                        (suggestion) => (
                            <Chip
                                key={suggestion}
                                label={`+ ${suggestion}`}
                                className={classes.suggestionChip}
                                onClick={() => addIngredient(suggestion)}
                            />
                        ),
                    )}
                </Stack>
            </Stack>

            <Field labelId="merchant-week-menu-ai-inspiration-label" labelFallback="Inspiration">
                <LocalizedStrict id="merchant-week-menu-ai-dialog-notes-field" attrs={{ placeholder: true }}>
                    <TextField
                        placeholder="Add your own ideas or rules, e.g. some certain requirements"
                        value={settings.inspiration}
                        multiline
                        minRows={2}
                        fullWidth
                        className={classes.select}
                        onChange={(event) => update({ inspiration: event.target.value })}
                    />
                </LocalizedStrict>
            </Field>
        </Stack>
    )
}
